/**
 * AI Assistant Drawer — embedded as an iframe (same pattern as WhatsAppDrawer).
 *
 * The actual chat UI lives in the `ai_assistent` repo and is hosted at
 * `<aiHostBaseUrl>/embed.html`. We:
 *   - Read `api_config.ai` via `useAiConfig()` to pick THIS user's token.
 *   - Mount an iframe pointed at the embed page (token + user injected).
 *   - Forward host-app events (ai-ask, ai-set-context, ai-open-thread, …)
 *     to the iframe via postMessage.
 *   - Listen for iframe events (ai-ready / ai-unread / ai-request-close) so
 *     we can show a pulsing FAB badge when the bot has news.
 *
 * Why an iframe?
 *   - Single source of truth: the chat widget code lives in ai_assistent and
 *     is bundled / hosted once. NihulKav, Priority, future apps all embed
 *     the same /embed.html — zero per-host duplication.
 *   - Scope enforcement is server-side (each token has a scopes[]), so the
 *     UI is safe to drop into any host without leaking capabilities.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
    Box, Fab, IconButton, Paper, Slide, Tooltip, Typography, Badge,
} from '@mui/material';
// MUI 5.0.3 (used by nexilcolab) doesn't re-export keyframes — pull from emotion directly.
import { keyframes } from '@emotion/react';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import CloseIcon from '@mui/icons-material/Close';

import { useAiConfig, AiHostConfig } from '../../../hooks/useAiConfig';
import { AI_EVENTS } from '../../../hooks/useAiAssistant';
import { AI_NOTIFICATIONS_READ_EVENT, setAiNotifications } from '../../../hooks/useAiNotifications';
import { sendMsgAll } from '../../../api/customer/files.api';
import { getWhatsappConfig } from '../../../hooks/useWhatsAppConfig';
import {
    isMetaWaTemplateServer,
    isWaPatternMsg,
    resolvePlainWhatsappMsgForSend,
    resolveWhatsappMsgForSend,
} from '../../../utils/whatsappPatterns';
import { SNACKBAR_OPEN } from 'store/actions';

export interface AiAssistantDrawerProps {
    /** Increment this number anywhere in the host app to force the panel open. */
    openSignal?: number;
    onOpenChange?: (open: boolean) => void;
}

type DisplayMode = 'fab' | 'mini' | 'drawer';

const MINI_W = 500;
const MINI_H = 720;
const DRAWER_W = 480;

const STORAGE_DISPLAY = 'ai-display-mode';
const STORAGE_MINI_W = 'ai-mini-w';
const STORAGE_MINI_H = 'ai-mini-h';
const MINI_MIN_W = 340;
const MINI_MAX_W = 720;
const MINI_MIN_H = 420;
const MINI_MAX_H = 900;

/** Soft repeating ring on the FAB while there's anything unread. */
const fabPulseKf = keyframes`
  0%   { box-shadow: 0 0 0 0   rgba(244, 67, 54, 0.55); }
  70%  { box-shadow: 0 0 0 14px rgba(244, 67, 54, 0); }
  100% { box-shadow: 0 0 0 0   rgba(244, 67, 54, 0); }
`;

/** Spinning conic ring around the FAB while a chat turn is in flight —
 *  the user collapsed the widget mid-answer and keeps working in the ERP. */
const fabWorkingKf = keyframes`
  from { transform: rotate(0deg); }
  to   { transform: rotate(360deg); }
`;

/** Quick "shake-and-glow" entrance for new bubble previews. */
const bubbleFlashKf = keyframes`
  0%   { box-shadow: 0 0 0 0    rgba(255,255,255,0); }
  35%  { box-shadow: 0 0 0 10px rgba(255,255,255,0.55); }
  100% { box-shadow: 0 0 0 0    rgba(255,255,255,0); }
`;

/** Notification preview shown above the FAB while the drawer is collapsed. */
interface BubbleNotif {
    id: string;
    severity: 'info' | 'success' | 'warning' | 'error';
    title: string;
    text: string;
    threadId?: string;
}

/** Window events used by the host-bridge contract (see ai-host-bridge in
 *  ai_assistent/frontend/src/embed.tsx). The drawer translates iframe
 *  postMessages into these events, and translates the host's reply event
 *  back into a postMessage to the iframe.
 *
 *  Host subscribers (e.g. AiDocumentCreatorBridge in MainLayout) listen
 *  for the request event and dispatch the result event with the same
 *  requestId so the iframe can resolve its pending promise. */
export const AI_HOST_BRIDGE_EVENTS = {
    REQUEST: 'ai-host-bridge-request',
    RESULT:  'ai-host-bridge-result',
} as const;

/** Explicit iframe → host capability contract. Unknown operations are rejected. */
export const AI_HOST_BRIDGE_ALLOWED_OPS = new Set([
    'request-fullscreen',
    'send-documents',
    'send-message',
    'create-document',
    'open-in-drawer',
    'open-document',
    'navigate',
    'open-orders',
    'open-send-dialog',
    'export-table',
]);

/**
 * Fired after the AI host bridge mutates ERP data that can change carteset /
 * debt balances (e.g. create-document). Pages like customer/all-debts listen
 * and merge-refresh that one client row — never replace the whole table.
 */
export const AI_CLIENT_DEBT_REFRESH_EVENT = 'ai-client-debt-refresh';

export type AiClientDebtRefreshDetail = {
    clientId: number;
    /** true when the entity is a supplier (sup all-debts), false for customer. */
    supplier?: boolean;
};

export function dispatchAiClientDebtRefresh(detail: AiClientDebtRefreshDetail): void {
    if (typeof window === 'undefined') return;
    const clientId = Number(detail?.clientId);
    if (!Number.isFinite(clientId) || clientId <= 0) return;
    try {
        window.dispatchEvent(new CustomEvent(AI_CLIENT_DEBT_REFRESH_EVENT, {
            detail: { clientId, supplier: Boolean(detail.supplier) },
        }));
    } catch { /* */ }
}

const BUBBLE_TTL_MS = 12_000;   // auto-dismiss after 12s (errors stay)
const BUBBLE_TTL_ERR_MS = 20_000;
const BUBBLE_MAX = 3;

/** Payload from the AI iframe for `send-documents` / `send-message` bridge ops. */
interface HostSendPayload {
    ids?: Array<number | string>;
    title?: string;
    msg?: string;
    message?: string;
    docTypes?: number[];
    doc_types?: number[];
    emails?: string[];
    phones?: string[];
    email_msg?: string;
    emailMsg?: string;
    special_arg?: string;
    external_var?: string[];
}

/** Plain text for email and non-Meta WhatsApp — never SMTP/WA pattern blocks. */
function resolveRegularMsgForSend(msg: string, fallback: string): string {
    const t = msg.trim();
    if (!t || isWaPatternMsg(msg)) return fallback;
    if (
        t === '#docs'
        || t.includes('#docs')
        || t === 'מסמכים'
        || t === 'חשבונית'
        || t.startsWith('היי חשבונית')
        || t.startsWith('חשבונית ')
    ) {
        return fallback;
    }
    return msg;
}

/**
 * Meta `#pattern=…` only when the *active* whatsapp sender (agent-matched
 * sender2/3/4, else primary) is server 4, 11, or 12 (Official Hub). Must use
 * getWhatsappConfig() so we never keep patterns while the jar delivers via a
 * Baileys sender2.
 */
function resolveWhatsappBody(
    rawMsg: string,
    docTypes: number[] | undefined,
    fallback: string,
): string {
    const waConfig = getWhatsappConfig();
    const useMeta = isMetaWaTemplateServer(waConfig);
    if (useMeta) {
        return resolveWhatsappMsgForSend({
            msg: rawMsg,
            docTypes,
            useMetaTemplates: true,
        });
    }
    const plain = resolvePlainWhatsappMsgForSend({
        msg: rawMsg,
        docTypes,
        useMetaTemplates: false,
        fallback,
    });
    // Last-resort guard: never deliver raw Meta template blocks on Baileys.
    if (isWaPatternMsg(plain)) {
        return resolvePlainWhatsappMsgForSend({
            msg: plain,
            docTypes,
            useMetaTemplates: false,
            fallback: fallback || 'היי #name\nמצורפת חשבונית',
        });
    }
    return plain;
}
/** If the iframe never handshakes, show a host close bar so the user isn't stuck. */
const EMBED_READY_TIMEOUT_MS = 18_000;

function _readDisplayMode(): DisplayMode {
    try {
        const v = localStorage.getItem(STORAGE_DISPLAY);
        if (v === 'mini' || v === 'drawer' || v === 'fab') return v;
    } catch { /* ssr */ }
    return 'fab';
}

function _readMiniSize(): { w: number; h: number } {
    try {
        const w = parseInt(localStorage.getItem(STORAGE_MINI_W) || '', 10);
        const h = parseInt(localStorage.getItem(STORAGE_MINI_H) || '', 10);
        /* Previous factory default was 420×600 — treat that as "never customized". */
        if (w === 420 && h === 600) return { w: MINI_W, h: MINI_H };
        return {
            w: Number.isFinite(w) && w >= MINI_MIN_W && w <= MINI_MAX_W ? w : MINI_W,
            h: Number.isFinite(h) && h >= MINI_MIN_H && h <= MINI_MAX_H ? h : MINI_H,
        };
    } catch { return { w: MINI_W, h: MINI_H }; }
}

/** Hosts the embed may postMessage from (aliases of the same Apache vhost). */
const EMBED_ORIGIN_ALIASES = [
    'https://ai.solvitcompany.com',
    'https://nihulkav.info',
    'https://www.nihulkav.info',
];

function _allowedEmbedOrigins(baseUrl: string): string[] {
    const out = new Set<string>(EMBED_ORIGIN_ALIASES);
    try {
        out.add(new URL(baseUrl).origin);
    } catch { /* */ }
    return [...out];
}

function _buildIframeSrc(cfg: AiHostConfig): string {
    // SECURITY: the bearer token is deliberately NOT in the URL — query
    // strings leak into browser history, Referer headers and server access
    // logs. The embed boots token-less, posts `ai-ready` (awaiting_token),
    // and we deliver credentials via `ai-set-token` postMessage below.
    let embedApiBase = cfg.apiBase;
    try {
        const api = new URL(cfg.apiBase);
        const embed = new URL(cfg.baseUrl);
        // The same-origin production vhost exposes the complete backend under
        // /api. Root-level prefixes are only legacy aliases and may not all be
        // present in Apache (for example /dc).
        if (api.origin === embed.origin && api.pathname === '/') {
            api.pathname = '/api';
            embedApiBase = api.toString().replace(/\/+$/, '');
        }
    } catch { /* keep the configured value */ }
    const qs = new URLSearchParams({
        user: cfg.userId,
        agent: cfg.agentId,
        api_base: embedApiBase,
        origin: typeof window !== 'undefined' ? window.location.origin : '*',
    });
    return `${cfg.baseUrl}/embed.html?${qs.toString()}`;
}

const AiAssistantDrawer = ({ openSignal, onOpenChange }: AiAssistantDrawerProps) => {
    const cfg = useAiConfig();
    const dispatch = useDispatch();

    const [display, setDisplay] = useState<DisplayMode>(_readDisplayMode);
    // Stable ref so the message-handler closure can read the *current* mode
    // without forcing the listener to re-bind on every display change.
    const displayRef = useRef<DisplayMode>(display);
    useEffect(() => { displayRef.current = display; }, [display]);
    const [miniSize, setMiniSize] = useState(_readMiniSize);
    /** Inner widget asked for the full chat area via the
     *  'request-fullscreen' host-bridge op (e.g. the DraftsDrawer opens).
     *  When true we promote the layout to `drawer` AND widen its width to
     *  100vw so the iframe-side drawer has room to lay out a real grid.
     *  Toggled back off when the inner posts another request-fullscreen
     *  with payload.mode='collapse' (or when the user closes the host). */
    const [fullscreen, setFullscreen] = useState(false);
    const [unread, setUnread] = useState(0);
    /** Inner widget reports an in-flight chat turn (`ai-busy`). */
    const [busy, setBusy] = useState(false);
    const [ready, setReady] = useState(false);
    const [embedError, setEmbedError] = useState<string | null>(null);
    const [bubbles, setBubbles] = useState<BubbleNotif[]>([]);
    const iframeRef = useRef<HTMLIFrameElement | null>(null);
    /** Actual iframe origin from the last `ai-ready` (may differ from baseUrl if Apache aliases). */
    const iframeOriginRef = useRef<string>('');

    /* ── Bubble preview helpers ────────────────────────────────────── */
    const dismissBubble = useCallback((id: string) => {
        setBubbles((prev) => prev.filter((b) => b.id !== id));
    }, []);
    const addBubble = useCallback((b: BubbleNotif) => {
        setBubbles((prev) => {
            // Dedup by id (the inner widget already gives us a unique id).
            if (prev.some((x) => x.id === b.id)) return prev;
            return [...prev, b].slice(-BUBBLE_MAX);
        });
        const ttl = b.severity === 'error' ? BUBBLE_TTL_ERR_MS : BUBBLE_TTL_MS;
        window.setTimeout(() => dismissBubble(b.id), ttl);
    }, [dismissBubble]);
    // Drop all bubbles when the drawer opens — the inner widget will surface
    // its own toast/UI from now on; the outer preview is only for the
    // collapsed (FAB) state.
    useEffect(() => {
        if (display !== 'fab') setBubbles([]);
    }, [display]);

    // Late-arriving messages from the host (ai-ask, ai-set-context, …) are
    // queued until the iframe handshakes back with `ai-ready`, then flushed.
    const pendingRef = useRef<any[]>([]);

    const iframeSrc = useMemo(() => (cfg ? _buildIframeSrc(cfg) : ''), [cfg]);

    // Reset handshake when the iframe URL changes (token / user switch).
    useEffect(() => {
        setReady(false);
        setEmbedError(null);
        iframeOriginRef.current = '';
    }, [iframeSrc]);

    // If the panel is open but embed never loads, surface an error + allow close.
    useEffect(() => {
        if (display === 'fab' || ready) {
            if (ready) setEmbedError(null);
            return;
        }
        const t = window.setTimeout(() => {
            setEmbedError('לא ניתן לטעון את עוזר ה-AI. בדוק את כתובת השרת והרשאות.');
        }, EMBED_READY_TIMEOUT_MS);
        return () => window.clearTimeout(t);
    }, [display, ready, iframeSrc]);

    /* ── Persist display mode ───────────────────────────────────────── */
    const switchDisplay = useCallback((mode: DisplayMode) => {
        setDisplay(mode);
        // mini and fullscreen are contradictory — every deliberate shrink to
        // the small popup (inner "מזער", bubble click, host events) must also
        // drop the fullscreen flag, otherwise the NEXT reopen/re-expand jumps
        // to 100vw and the inner widget keeps a stale "I'm fullscreen" state
        // (the "compare view cut inside the small panel" bug). Closing to the
        // FAB deliberately keeps it so reopen restores what the user saw.
        if (mode === 'mini') setFullscreen(false);
        try { localStorage.setItem(STORAGE_DISPLAY, mode); } catch { /* */ }
    }, []);

    /* ── Reopen from FAB restores whatever the user was looking at ────
     *  `fullscreen` (the inner widget's docs-manager / pending-orders split
     *  compare) is never reset when the panel is closed — only the outer
     *  chrome hides to the FAB. So if the user closed WHILE in that expanded
     *  view, reopening should go straight back to the wide drawer instead of
     *  always forcing the small `mini` popup (which used to fight with the
     *  still-fullscreen inner state and/or throw the user back to a tiny
     *  view of what they were doing). Plain chat/mini closes still reopen
     *  to `mini` as before. */
    const fullscreenRef = useRef(fullscreen);
    useEffect(() => { fullscreenRef.current = fullscreen; }, [fullscreen]);
    /** Where the user was BEFORE the inner widget requested fullscreen —
     *  restored on the collapse request so "פתח השוואה במסך מלא ← חזרה"
     *  lands back in the small popup they came from instead of stranding
     *  them in the 480px drawer they never asked for. */
    const preFullscreenDisplayRef = useRef<DisplayMode>('mini');
    const reopen = useCallback(() => {
        switchDisplay(fullscreenRef.current ? 'drawer' : 'mini');
    }, [switchDisplay]);

    useEffect(() => { onOpenChange?.(display !== 'fab'); }, [display, onOpenChange]);

    useEffect(() => {
        if (openSignal && openSignal > 0 && display === 'fab') switchDisplay('mini');
    }, [openSignal, display, switchDisplay]);

    /** Host-side send from the AI iframe — email and WhatsApp get separate bodies. */
    const handleHostSend = useCallback(async (
        payload: HostSendPayload | null,
        relayResult: (ok: boolean, error?: string) => void,
    ) => {
        try {
            const ids = (payload?.ids || [])
                .map((id) => Number(id))
                .filter((id) => Number.isFinite(id) && id > 0);
            if (!ids.length) {
                relayResult(false, 'אין מסמכים לשליחה');
                return;
            }

            const title = String(payload?.title || 'שליחת מסמכים');
            const rawMsg = String(payload?.msg || payload?.message || '');
            const docTypes = payload?.docTypes || payload?.doc_types;
            const emails = (payload?.emails || [])
                .map((x) => String(x).trim())
                .filter(Boolean);
            const phones = (payload?.phones || [])
                .map((x) => String(x).trim())
                .filter(Boolean);
            const fallback = String(
                payload?.email_msg || payload?.emailMsg || title || 'מסמכים מצורפים',
            );
            const emailMsg = resolveRegularMsgForSend(rawMsg, fallback);
            const whatsappMsg = resolveWhatsappBody(rawMsg, docTypes, fallback);
            const specialArg = payload?.special_arg;
            const externalVar = payload?.external_var;

            if (emails.length) {
                await sendMsgAll(
                    ids,
                    emailMsg,
                    title,
                    docTypes,
                    emails,
                    undefined,
                    undefined,
                    specialArg,
                    externalVar,
                );
            }
            if (phones.length) {
                await sendMsgAll(
                    ids,
                    whatsappMsg,
                    title,
                    docTypes,
                    undefined,
                    phones,
                    undefined,
                    specialArg,
                    externalVar,
                );
            }

            dispatch({
                type: SNACKBAR_OPEN,
                open: true,
                message: 'נשלח בהצלחה!',
                variant: 'alert',
                alertSeverity: 'success',
            });
            relayResult(true);
        } catch (err: any) {
            dispatch({
                type: SNACKBAR_OPEN,
                open: true,
                message: 'שליחה נכשלה',
                variant: 'alert',
                alertSeverity: 'error',
            });
            relayResult(false, err?.message || String(err));
        }
    }, [dispatch]);

    /* ── Helper to send a message to the iframe (with queueing) ────── */
    const post = useCallback((msg: any) => {
        const win = iframeRef.current?.contentWindow;
        const targetOrigin =
            iframeOriginRef.current
            || (cfg ? new URL(cfg.baseUrl).origin : '*');
        if (!win || !ready) {
            pendingRef.current.push(msg);
            return;
        }
        try { win.postMessage(msg, targetOrigin); } catch { /* */ }
    }, [cfg, ready]);

    // Flush queued messages when the iframe finishes handshaking.
    useEffect(() => {
        if (!ready) return;
        const win = iframeRef.current?.contentWindow;
        if (!win) return;
        const targetOrigin =
            iframeOriginRef.current
            || (cfg ? new URL(cfg.baseUrl).origin : '*');
        const pending = pendingRef.current.slice();
        pendingRef.current = [];
        for (const msg of pending) {
            try { win.postMessage(msg, targetOrigin); } catch { /* */ }
        }
    }, [ready, cfg]);

    /* ── Credential delivery — postMessage, never the URL ─────────────
     *  The token-less embed boots, posts `ai-ready` (awaiting_token) and we
     *  answer with `ai-set-token`. Queued via post() until the handshake,
     *  so ordering is guaranteed. Re-posting after `ready` flips is a
     *  harmless no-op on the embed side (same token → no re-render). */
    useEffect(() => {
        if (!cfg?.token) return;
        post({
            type: 'ai-set-token',
            token: cfg.token,
            user: cfg.userId,
            agent: cfg.agentId,
        });
    }, [cfg, post]);

    /* ── Tell the inner widget which display we're showing so the
          expand/contract icon in its header reflects reality. The `fullscreen`
          flag is the ACK the inner compare view waits for before rendering its
          wide split layout — mode:'drawer' alone can mean the narrow 480px
          panel, which would clip that layout. */
    useEffect(() => {
        if (display === 'mini' || display === 'drawer') {
            post({ type: 'ai-display', mode: display, fullscreen });
        }
    }, [display, fullscreen, post]);

    /* ── Tell the inner widget it just came back from being closed (FAB),
     *  so it can refetch whatever draft/list is currently open instead of
     *  showing a stale snapshot from before the panel was hidden. */
    const prevDisplayRef = useRef<DisplayMode>(display);
    useEffect(() => {
        if (prevDisplayRef.current === 'fab' && display !== 'fab') {
            post({ type: 'ai-reopened' });
        }
        prevDisplayRef.current = display;
    }, [display, post]);

    /* ── Listen for iframe → host messages ─────────────────────────── */
    useEffect(() => {
        if (!cfg) return;
        const allowedOrigins = _allowedEmbedOrigins(cfg.baseUrl);
        const handler = (e: MessageEvent) => {
            if (e.origin && allowedOrigins.length && !allowedOrigins.includes(e.origin)) return;
            const data: any = e.data;
            if (!data || typeof data !== 'object') return;
            // Capture display in a stable ref the switch arms can read.
            // (We close over `display` directly below.)
            switch (data.type) {
                case 'ai-ready':
                    if (e.origin) iframeOriginRef.current = e.origin;
                    setReady(true);
                    // Re-sync: the iframe may have (re)loaded while we're
                    // already showing mini/drawer — replay the current chrome
                    // state so the inner header + compare gating match reality
                    // instead of the inner's defaults.
                    if (displayRef.current === 'mini' || displayRef.current === 'drawer') {
                        post({
                            type: 'ai-display',
                            mode: displayRef.current,
                            fullscreen: fullscreenRef.current,
                        });
                    }
                    break;
                case 'ai-unread':
                    setUnread(Number(data.total || 0));
                    break;
                case 'ai-busy':
                    setBusy(Boolean((data as any).busy));
                    break;
                case 'ai-request-close':
                    switchDisplay('fab');
                    break;
                case 'ai-request-resize': {
                    // Inner widget asked to grow/shrink — the iframe owns
                    // the chrome so the request must come back here.
                    const mode = (data as any).mode;
                    if (mode === 'mini' || mode === 'drawer') {
                        switchDisplay(mode);
                    } else if (mode === 'toggle') {
                        switchDisplay(displayRef.current === 'drawer' ? 'mini' : 'drawer');
                    }
                    break;
                }
                case 'ai-notification': {
                    // Only render bubble preview when we're collapsed —
                    // otherwise the inner panel already shows a toast.
                    if (displayRef.current !== 'fab') break;
                    const id = String((data as any).id || `${Date.now()}-${Math.random()}`);
                    const sev = (data as any).severity;
                    addBubble({
                        id,
                        severity: (sev === 'success' || sev === 'error' || sev === 'warning')
                            ? sev : 'info',
                        title: String((data as any).title || ''),
                        text:  String((data as any).text  || ''),
                        threadId: (data as any).thread_id || undefined,
                    });
                    break;
                }
                case 'ai-notifications':
                    // Server-side inbox mirror (scheduled task results,
                    // reminders) → header bell via useAiNotifications.
                    setAiNotifications((data as any).items);
                    break;
                case 'ai-thread':
                case 'ai-view':
                case 'ai-pong':
                    // Reserved for future host-side reactions (e.g. surface
                    // an in-app toast when the bot replies in the background).
                    break;
                case 'ai-host-bridge': {
                    // Inner widget asks the host to perform an action it
                    // can't (ERP doc creation, open big drawer, …).
                    // Dispatch as a window event so feature-specific
                    // subscribers (AiDocumentCreatorBridge etc.) can pick
                    // it up without having to know about the iframe.
                    const detail = {
                        op: String((data as any).op || ''),
                        requestId: String((data as any).request_id || ''),
                        awaitResult: Boolean((data as any).await_result),
                        payload: (data as any).payload ?? null,
                    };
                    if (!detail.requestId || !detail.op) break;
                    if (!AI_HOST_BRIDGE_ALLOWED_OPS.has(detail.op)) {
                        if (detail.awaitResult) {
                            post({
                                type: 'ai-host-bridge-result',
                                request_id: detail.requestId,
                                ok: false,
                                error: 'פעולת host bridge אינה מורשית',
                            });
                        }
                        break;
                    }
                    // 'request-fullscreen' is consumed locally — widen
                    // the host's Drawer to 100vw so the iframe-side
                    // DraftsDrawer has room. The inner widget already
                    // toggled its overlay state; we just need to grow
                    // the chrome. The op MAY carry payload.mode='collapse'
                    // to ask for the opposite transition.
                    if (detail.op === 'request-fullscreen') {
                        const mode = (detail.payload as any)?.mode;
                        const next = mode === 'collapse' ? false : true;
                        if (next && !fullscreenRef.current) {
                            // Entering fullscreen — remember the origin so the
                            // matching collapse returns the user THERE (mini
                            // stays mini) instead of the 480px drawer.
                            preFullscreenDisplayRef.current = displayRef.current;
                        }
                        setFullscreen(next);
                        if (next && displayRef.current !== 'drawer') {
                            switchDisplay('drawer');
                        }
                        if (!next && displayRef.current === 'drawer'
                            && preFullscreenDisplayRef.current === 'mini') {
                            switchDisplay('mini');
                        }
                        break;
                    }
                    // Document / message sends — email always plain text;
                    // WhatsApp uses Meta `#pattern=…` only on server 4/11.
                    if (detail.op === 'send-documents' || detail.op === 'send-message') {
                        const awaitResult = Boolean(detail.awaitResult);
                        const relayResult = (ok: boolean, error?: string) => {
                            if (!awaitResult) return;
                            post({
                                type: 'ai-host-bridge-result',
                                request_id: detail.requestId,
                                ok,
                                error: error || undefined,
                            });
                        };
                        void handleHostSend(
                            (detail.payload as HostSendPayload | null) ?? null,
                            relayResult,
                        );
                        break;
                    }
                    window.dispatchEvent(
                        new CustomEvent(AI_HOST_BRIDGE_EVENTS.REQUEST, { detail }),
                    );
                    break;
                }
            }
        };
        window.addEventListener('message', handler);
        return () => window.removeEventListener('message', handler);
    }, [cfg, switchDisplay, addBubble, handleHostSend, post]);

    /* ── Host bridge: forward result events back into the iframe ──────
     *  Subscribers (AiDocumentCreatorBridge etc.) dispatch
     *  `ai-host-bridge-result` after they finish; we relay it back to the
     *  iframe via postMessage so ChatWidget can resolve its promise. */
    useEffect(() => {
        const onResult = (e: Event) => {
            const detail: any = (e as CustomEvent).detail || {};
            if (!detail.requestId) return;
            post({
                type: 'ai-host-bridge-result',
                request_id: String(detail.requestId),
                ok: Boolean(detail.ok),
                document_id: detail.documentId ?? detail.document_id,
                error: detail.error ? String(detail.error) : undefined,
                result: detail.result,
            });
        };
        window.addEventListener(AI_HOST_BRIDGE_EVENTS.RESULT, onResult as EventListener);
        return () => window.removeEventListener(AI_HOST_BRIDGE_EVENTS.RESULT, onResult as EventListener);
    }, [post]);

    /* ── Host-app events (from useAiAssistant.ts) ──────────────────── */
    useEffect(() => {
        if (!cfg) return;
        const onAsk = (e: Event) => {
            const detail: any = (e as CustomEvent).detail || {};
            // Make sure the panel is visible so the user actually sees the answer.
            if (display === 'fab') switchDisplay('mini');
            if (detail.context) post({ type: 'ai-set-context', context: detail.context });
            if (detail.agentId) post({ type: 'ai-set-agent', agent_id: detail.agentId });
            if (detail.prompt) {
                post({
                    type: 'ai-prefill',
                    message: String(detail.prompt),
                    send: detail.send !== false,
                });
            }
        };
        const onOpen = (e: Event) => {
            const detail: any = (e as CustomEvent).detail || {};
            if (display === 'fab') switchDisplay('mini');
            if (detail.agentId) post({ type: 'ai-set-agent', agent_id: detail.agentId });
        };
        const onOpenThread = (e: Event) => {
            const detail: any = (e as CustomEvent).detail || {};
            if (display === 'fab') switchDisplay('mini');
            if (detail.threadId) post({ type: 'ai-open-thread', thread_id: String(detail.threadId) });
        };
        const onSetContext = (e: Event) => {
            const detail: any = (e as CustomEvent).detail || {};
            post({ type: 'ai-set-context', context: detail.context || null });
        };
        const onSetAgent = (e: Event) => {
            const detail: any = (e as CustomEvent).detail || {};
            if (detail.agentId) post({ type: 'ai-set-agent', agent_id: detail.agentId });
        };
        const onOpenView = (e: Event) => {
            const detail: any = (e as CustomEvent).detail || {};
            const view = String(detail.view || 'docs-manager');
            if (display === 'fab') switchDisplay('drawer');
            if (detail.agentId) post({ type: 'ai-set-agent', agent_id: detail.agentId });
            post({
                type: 'ai-open-view',
                view,
                draft_id: detail.draftId ? String(detail.draftId) : undefined,
            });
        };
        const onNotificationsRead = (e: Event) => {
            const detail: any = (e as CustomEvent).detail || {};
            post({
                type: 'ai-notifications-read',
                ids: Array.isArray(detail.ids) ? detail.ids.map(String) : [],
            });
        };
        window.addEventListener(AI_EVENTS.ASK, onAsk);
        window.addEventListener(AI_EVENTS.OPEN, onOpen);
        window.addEventListener(AI_EVENTS.OPEN_THREAD, onOpenThread);
        window.addEventListener(AI_EVENTS.OPEN_VIEW, onOpenView);
        window.addEventListener(AI_EVENTS.SET_CONTEXT, onSetContext);
        window.addEventListener(AI_EVENTS.SET_AGENT, onSetAgent);
        window.addEventListener(AI_NOTIFICATIONS_READ_EVENT, onNotificationsRead);
        return () => {
            window.removeEventListener(AI_EVENTS.ASK, onAsk);
            window.removeEventListener(AI_EVENTS.OPEN, onOpen);
            window.removeEventListener(AI_EVENTS.OPEN_THREAD, onOpenThread);
            window.removeEventListener(AI_EVENTS.OPEN_VIEW, onOpenView);
            window.removeEventListener(AI_EVENTS.SET_CONTEXT, onSetContext);
            window.removeEventListener(AI_EVENTS.SET_AGENT, onSetAgent);
            window.removeEventListener(AI_NOTIFICATIONS_READ_EVENT, onNotificationsRead);
        };
    }, [cfg, display, post, switchDisplay]);

    /* ── Mini resize (top-left corner handle, RTL-friendly) ──────────
     *  Mouse AND touch: phones/tablets could never resize the mini panel
     *  before (the handle only listened to mousemove). */
    const beginMiniResize = useCallback((startX: number, startY: number) => {
        const startW = miniSize.w;
        const startH = miniSize.h;
        const apply = (cx: number, cy: number) => {
            const dw = cx - startX;
            const dh = startY - cy;
            setMiniSize({
                w: Math.min(MINI_MAX_W, Math.max(MINI_MIN_W, startW + dw)),
                h: Math.min(MINI_MAX_H, Math.max(MINI_MIN_H, startH + dh)),
            });
        };
        const persist = () => {
            document.body.style.cursor = '';
            document.body.style.userSelect = '';
            setMiniSize((s) => {
                try {
                    localStorage.setItem(STORAGE_MINI_W, String(s.w));
                    localStorage.setItem(STORAGE_MINI_H, String(s.h));
                } catch { /* */ }
                return s;
            });
        };
        const onMouseMove = (ev: MouseEvent) => { ev.preventDefault(); apply(ev.clientX, ev.clientY); };
        const onMouseUp = () => {
            document.removeEventListener('mousemove', onMouseMove);
            document.removeEventListener('mouseup', onMouseUp);
            persist();
        };
        const onTouchMove = (ev: TouchEvent) => {
            const t = ev.touches[0];
            if (!t) return;
            ev.preventDefault();
            apply(t.clientX, t.clientY);
        };
        const onTouchEnd = () => {
            document.removeEventListener('touchmove', onTouchMove);
            document.removeEventListener('touchend', onTouchEnd);
            persist();
        };
        document.body.style.cursor = 'nw-resize';
        document.body.style.userSelect = 'none';
        document.addEventListener('mousemove', onMouseMove);
        document.addEventListener('mouseup', onMouseUp);
        document.addEventListener('touchmove', onTouchMove, { passive: false });
        document.addEventListener('touchend', onTouchEnd);
    }, [miniSize]);

    const handleMiniResize = useCallback((e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        beginMiniResize(e.clientX, e.clientY);
    }, [beginMiniResize]);

    const handleMiniResizeTouch = useCallback((e: React.TouchEvent) => {
        const t = e.touches[0];
        if (!t) return;
        e.stopPropagation();
        beginMiniResize(t.clientX, t.clientY);
    }, [beginMiniResize]);

    /* ── No config → render nothing (this user has no AI access). ──── */
    if (!cfg || !iframeSrc) return null;

    /** Host-only chrome when the iframe is blank / still loading — avoids a stuck empty panel. */
    const showHostFallback = display !== 'fab' && (!ready || Boolean(embedError));
    const hostFallbackBar = showHostFallback ? (
        <Box sx={{
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
            px: 1,
            py: 0.75,
            borderBottom: '1px solid',
            borderColor: 'divider',
            bgcolor: '#7c4dff',
            color: '#fff',
            direction: 'rtl',
        }}>
            <Typography variant="subtitle2" fontWeight={700} noWrap sx={{ flex: 1 }}>
                {embedError ? 'עוזר AI — שגיאת חיבור' : 'עוזר AI — טוען…'}
            </Typography>
            <IconButton
                size="small"
                aria-label="סגור"
                onClick={() => switchDisplay('fab')}
                sx={{ color: '#fff' }}
            >
                <CloseIcon fontSize="small" />
            </IconButton>
        </Box>
    ) : null;

    const embedErrorBody = embedError ? (
        <Box sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            p: 2,
            gap: 1.5,
            direction: 'rtl',
            textAlign: 'center',
            bgcolor: 'background.paper',
        }}>
            <Typography variant="body2" color="text.secondary">
                {embedError}
            </Typography>
            <Typography variant="caption" color="text.disabled" sx={{ wordBreak: 'break-all' }}>
                {cfg.baseUrl}/embed.html
            </Typography>
            <IconButton
                onClick={() => switchDisplay('fab')}
                sx={{ bgcolor: 'action.hover' }}
                aria-label="סגור"
            >
                <CloseIcon />
            </IconButton>
        </Box>
    ) : null;

    /* ── Bubble preview stack ABOVE the FAB ─────────────────────────────
       Shows the latest 1-3 task / message notifications so the user knows
       what arrived without expanding the panel. Only rendered while the
       drawer is collapsed (the inner panel handles toasts when open). */
    const bubbleStack = (display === 'fab' && bubbles.length > 0) ? (
        <Box sx={{
            position: 'fixed',
            bottom: 90 + 56 + 12,    // FAB bottom + height + gap
            right: 24,
            zIndex: 1305,
            display: 'flex',
            flexDirection: 'column-reverse',
            gap: 0.75,
            maxWidth: 320,
            pointerEvents: 'none',
        }}>
            {bubbles.slice(-BUBBLE_MAX).map((b) => (
                <Slide key={b.id} direction="left" in mountOnEnter unmountOnExit>
                    <Paper
                        elevation={6}
                        onClick={() => {
                            // Open the drawer; if the inner needs to jump
                            // to a specific thread, ask via postMessage.
                            switchDisplay('mini');
                            if (b.threadId) {
                                post({ type: 'ai-open-thread', thread_id: b.threadId });
                            }
                            dismissBubble(b.id);
                        }}
                        sx={{
                            pointerEvents: 'auto',
                            cursor: 'pointer',
                            borderRadius: '14px 14px 4px 14px',
                            px: 1.5, py: 1,
                            bgcolor: b.severity === 'success' ? '#2e7d32'
                                  : b.severity === 'error'   ? '#c62828'
                                  : b.severity === 'warning' ? '#ef6c00'
                                  : '#7c4dff',
                            color: '#fff',
                            direction: 'rtl',
                            animation: `${bubbleFlashKf} 1.1s ease-out`,
                            boxShadow: '0 4px 16px rgba(0,0,0,0.18)',
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: 0.75,
                        }}
                    >
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography sx={{
                                display: 'block', fontWeight: 700, fontSize: '0.78rem',
                                lineHeight: 1.2,
                            }}>
                                {b.title}
                            </Typography>
                            {b.text && (
                                <Typography sx={{
                                    display: '-webkit-box',
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: 'vertical',
                                    overflow: 'hidden',
                                    fontSize: '0.74rem',
                                    opacity: 0.95,
                                    mt: 0.25,
                                }}>
                                    {b.text}
                                </Typography>
                            )}
                        </Box>
                        <IconButton
                            size="small"
                            sx={{ color: '#fff', p: 0.25, mt: -0.5 }}
                            onClick={(e) => { e.stopPropagation(); dismissBubble(b.id); }}
                        >
                            <CloseIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                    </Paper>
                </Slide>
            ))}
        </Box>
    ) : null;

    /* ── FAB (collapsed) ───────────────────────────────────────────── */
    const fab = (
        <Tooltip title={busy ? 'העוזר עובד על התשובה…' : 'עוזר AI'}>
            <Fab
                onClick={reopen}
                sx={{
                    position: 'fixed',
                    bottom: 90,
                    right: 24,
                    zIndex: 1300,
                    width: 56,
                    height: 56,
                    bgcolor: '#7c4dff',
                    color: '#fff',
                    boxShadow: 4,
                    overflow: 'visible',
                    '&:hover': { bgcolor: '#651fff' },
                    ...(unread > 0 && {
                        animation: `${fabPulseKf} 1.6s ease-out infinite`,
                    }),
                    // "Working" ring: a spinning conic arc just outside the
                    // FAB so the user sees the answer is still being prepared.
                    ...(busy && {
                        '&::before': {
                            content: '""',
                            position: 'absolute',
                            inset: -4,
                            borderRadius: '50%',
                            background: 'conic-gradient(from 0deg, rgba(124,77,255,0) 0deg, #b388ff 90deg, rgba(124,77,255,0) 180deg)',
                            WebkitMask: 'radial-gradient(farthest-side, transparent calc(100% - 3px), #000 calc(100% - 3px))',
                            mask: 'radial-gradient(farthest-side, transparent calc(100% - 3px), #000 calc(100% - 3px))',
                            animation: `${fabWorkingKf} 1.1s linear infinite`,
                            pointerEvents: 'none',
                        },
                    }),
                }}
            >
                <Badge color="error" badgeContent={unread} max={99} overlap="circular" invisible={unread === 0}>
                    <SmartToyIcon />
                </Badge>
            </Fab>
        </Tooltip>
    );

    /* The iframe's inner ChatWidget already renders its own purple header
       (with title, cost chip, expand/contract, close). We do NOT render an
       outer header here to avoid the duplicate / overlapping chrome the
       user noticed. The inner buttons proxy back to us via postMessage:
         - X        → 'ai-request-close'  → switchDisplay('fab')
         - expand   → 'ai-request-resize' → switchDisplay('mini'|'drawer')
       The resize handle in the corner is the only chrome we add. */

    /* ── The iframe — kept mounted at all times so SSE / polling / chat
          state survives mini⇄drawer⇄fab transitions. In fab mode it's
          parked off-screen (1×1 px, hidden, no pointer events) so the
          inner widget keeps receiving real-time notification events
          which we forward as bubble previews above the FAB. */
    const iframe = embedError ? null : (
        <iframe
            ref={iframeRef}
            src={iframeSrc}
            title="AI Assistant"
            allow="clipboard-write; clipboard-read"
            style={{
                flex: 1,
                width: '100%',
                border: 'none',
                background: 'transparent',
                // Keep the iframe laid out (not display:none) while the host
                // chrome shows "loading". display:none can abort a cold load
                // of a new embed origin so ai-ready never arrives.
                visibility: showHostFallback && !ready ? 'hidden' : 'visible',
                position: showHostFallback && !ready ? 'absolute' : 'relative',
                pointerEvents: showHostFallback && !ready ? 'none' : 'auto',
            }}
        />
    );

    /* ── Single persistent shell ──────────────────────────────────────
       CRITICAL: the iframe must stay in ONE parent across fab⇄mini⇄drawer
       and fullscreen toggles. Moving an <iframe> to a different DOM parent
       forces the browser to RELOAD it, which remounts the inner ChatWidget
       and throws the user back to the default (chat) tab with a loading
       flash — exactly the "it reset the component" bug. So we render the
       container once and only vary its geometry/chrome via CSS. */
    const isFab = display === 'fab';
    const isMini = display === 'mini';

    const containerSx = isFab
        ? {
              // Parked off-screen — keeps SSE/polling + inner state alive.
              position: 'fixed' as const,
              left: -9999, top: -9999,
              width: 1, height: 1,
              overflow: 'hidden',
              pointerEvents: 'none' as const,
              opacity: 0,
              zIndex: -1,
              display: 'flex',
              flexDirection: 'column' as const,
          }
        : isMini
        ? {
              position: 'fixed' as const,
              bottom: 16,
              right: 16,
              // Clamp to the viewport so the remembered desktop size never
              // overflows a small phone screen.
              width: `min(${miniSize.w}px, calc(100vw - 24px))`,
              height: `min(${miniSize.h}px, calc(100vh - 24px))`,
              // 1301: one step above the WhatsApp drawer band (1300) so an
              // OPEN AI panel deterministically wins instead of DOM order.
              zIndex: 1301,
              borderRadius: 3,
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column' as const,
              boxShadow: '0 8px 40px rgba(0,0,0,0.18)',
              border: '1px solid',
              borderColor: 'divider',
              bgcolor: 'background.paper',
          }
        : {
              // Drawer: right-anchored, full height. `fullscreen` (DraftsDrawer
              // open) widens to the whole viewport. Width-only transition so
              // the iframe is never reparented.
              position: 'fixed' as const,
              top: 0,
              right: 0,
              height: '100vh',
              width: fullscreen ? '100vw' : { xs: '100vw', sm: DRAWER_W },
              transition: 'width 200ms ease',
              zIndex: 1301,
              bgcolor: 'background.paper',
              borderLeft: '1px solid',
              borderColor: 'divider',
              boxShadow: '-4px 0 24px rgba(0,0,0,0.08)',
              display: 'flex',
              flexDirection: 'column' as const,
          };

    return (
        <>
            {isFab && bubbleStack}
            {isFab && fab}
            <Box aria-hidden={isFab} sx={containerSx}>
                {isMini && (
                    /* Top-left resize handle (RTL-friendly). Above the iframe
                       via zIndex so dragging doesn't get swallowed. Larger
                       hit-area + touch support for phones. */
                    <Box
                        onMouseDown={handleMiniResize}
                        onTouchStart={handleMiniResizeTouch}
                        sx={{
                            position: 'absolute', top: 0, left: 0, width: 28, height: 28,
                            cursor: 'nw-resize', zIndex: 20, touchAction: 'none',
                            '&::before': {
                                content: '""', position: 'absolute', top: 3, left: 3,
                                width: 8, height: 8,
                                borderTop: '2px solid rgba(124, 77, 255, 0.6)',
                                borderLeft: '2px solid rgba(124, 77, 255, 0.6)',
                            },
                        }}
                    />
                )}
                {hostFallbackBar}
                {embedErrorBody}
                {iframe}
            </Box>
        </>
    );
};

export default AiAssistantDrawer;
